import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import ContactModal from './ContactModal';
import CustomerAuthModal from './CustomerAuthModal';
import CustomerProfile from './CustomerProfile';
import './Navbar.css';

const Navbar = ({ isScrolled }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFlagDropdownOpen, setIsFlagDropdownOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const timeoutRef = useRef(null);
  const location = useLocation();
  const { customer, hasPriceAccess, logout } = useCustomerAuth();

  // Check if we're on tienda route, soporte route, or their sub-routes
  const isTiendaRoute = location.pathname.startsWith('/tienda');
  const isSoporteRoute = location.pathname.startsWith('/soporte');
  
  const navbarClass = `navbar ${isScrolled || isMenuOpen || isTiendaRoute || isSoporteRoute ? 'navbar-scrolled' : ''}`;

  const handleLogoClick = (e) => {
    // Always scroll to top when navigating to homepage
    // Use setTimeout to ensure navigation completes before scrolling
    setTimeout(() => {
      if (window.location.pathname === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleTiendaClick = () => {
    // Scroll to top when clicking on tienda link
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFlagClick = () => {
    setIsFlagDropdownOpen(!isFlagDropdownOpen);
  };

  const handleColombiaFlagClick = () => {
    // Clear timeout when user clicks Colombian flag
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    window.location.href = 'https://distracker.co';
  };

  const handleDropdownMouseEnter = () => {
    // Clear timeout when user hovers over dropdown
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleDropdownMouseLeave = () => {
    // Restart timeout when user leaves dropdown
    if (isFlagDropdownOpen) {
      timeoutRef.current = setTimeout(() => {
        setIsFlagDropdownOpen(false);
      }, 1500);
    }
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    setIsContactModalOpen(true);
  };

  const handleAuthClick = () => {
    if (customer) {
      setIsProfileOpen(true);
    } else {
      setIsAuthModalOpen(true);
    }
  };

  const handleLogout = () => {
    logout();
    setIsProfileOpen(false);
    setIsCustomerDropdownOpen(false);
  };

  const handleCustomerClick = () => {
    if (customer) {
      setIsCustomerDropdownOpen(!isCustomerDropdownOpen);
    } else {
      setIsAuthModalOpen(true);
    }
  };

  const handleProfileClick = () => {
    setIsProfileOpen(true);
    setIsCustomerDropdownOpen(false);
  };

  const handleMenuClick = () => {
    setIsMenuOpen(false);
  };

  // Auto-close dropdowns after 1.5 seconds
  useEffect(() => {
    if (isFlagDropdownOpen || isCustomerDropdownOpen) {
      timeoutRef.current = setTimeout(() => {
        setIsFlagDropdownOpen(false);
        setIsCustomerDropdownOpen(false);
      }, 1500);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isFlagDropdownOpen, isCustomerDropdownOpen]);

  return (
    <>
      <nav className={navbarClass}>
        <div className="navbar-container">
          <div className="navbar-logo">
            <Link to="/" onClick={handleLogoClick}>
              <img src="/assets/images/Distracker.png" alt="Distracker" />
            </Link>
          </div>
          
          <div className={`navbar-menu ${isMenuOpen ? 'active' : ''}`}>
            <Link to="/tienda" className="nav-link" onClick={() => { handleTiendaClick(); handleMenuClick(); }}>Productos</Link>
            <Link to="/soporte" className="nav-link" onClick={handleMenuClick}>Soporte</Link>
            <a href="#contact" className="nav-link" onClick={(e) => { handleContactClick(e); handleMenuClick(); }}>Contacto</a>
            {customer && (
              <>
                <button className="nav-link mobile-customer-option" onClick={() => { handleProfileClick(); handleMenuClick(); }}>
                  Mi Perfil
                </button>
                <button className="nav-link mobile-customer-option logout-option" onClick={() => { handleLogout(); handleMenuClick(); }}>
                  Cerrar Sesión
                </button>
              </>
            )}
          </div>

          <div className="navbar-actions">
            <div className="flag-container">
              <button className="flag-button" onClick={handleFlagClick}>
                <img src="/assets/images/flag-mexico.min.svg" alt="Mexico" className="flag-icon" />
              </button>
              <div 
                className={`flag-dropdown ${isFlagDropdownOpen ? 'active' : ''}`}
                onMouseEnter={handleDropdownMouseEnter}
                onMouseLeave={handleDropdownMouseLeave}
              >
                <button className="colombia-flag" onClick={handleColombiaFlagClick}>
                  <img src="/assets/images/flag-colombia.min.svg" alt="Colombia" className="flag-icon" />
                </button>
              </div>
            </div>
            <div className="customer-container">
              <button className="btn-login" onClick={handleCustomerClick}>
                {customer ? `${customer.firstName} ${customer.lastName}` : 'Iniciar Sesión'}
                {hasPriceAccess && <span className="price-access-badge" title="Acceso a precios">💰</span>}
                {customer && <span className="dropdown-arrow">▼</span>}
              </button>
              
              {customer && (
                <div 
                  className={`customer-dropdown ${isCustomerDropdownOpen ? 'active' : ''}`}
                  onMouseEnter={() => {
                    if (timeoutRef.current) {
                      clearTimeout(timeoutRef.current);
                    }
                  }}
                  onMouseLeave={() => {
                    if (isCustomerDropdownOpen) {
                      timeoutRef.current = setTimeout(() => {
                        setIsCustomerDropdownOpen(false);
                      }, 1500);
                    }
                  }}
                >
                  <button className="dropdown-item" onClick={handleProfileClick}>
                    Mi Perfil
                  </button>
                  <button className="dropdown-item logout-item" onClick={handleLogout}>
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="navbar-right">
            <div className="mobile-flag-container">
              <button className="flag-button mobile-flag" onClick={handleFlagClick}>
                <img src="/assets/images/flag-mexico.min.svg" alt="Mexico" className="flag-icon" />
              </button>
              <div 
                className={`flag-dropdown mobile-dropdown ${isFlagDropdownOpen ? 'active' : ''}`}
                onMouseEnter={handleDropdownMouseEnter}
                onMouseLeave={handleDropdownMouseLeave}
              >
                <button className="colombia-flag" onClick={handleColombiaFlagClick}>
                  <img src="/assets/images/flag-colombia.min.svg" alt="Colombia" className="flag-icon" />
                </button>
              </div>
            </div>
            <div 
              className={`hamburger ${isMenuOpen ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </nav>
      
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
      
      <CustomerAuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)} 
      />
      
      <CustomerProfile 
        isOpen={isProfileOpen} 
        onClose={() => setIsProfileOpen(false)} 
      />
    </>
  );
};

export default Navbar; 